<template>
	<ActivityResourceCard class="opportunity-card">
		<OpportunityCardFlagIcon class="opportunity-icon" :type="opportunity.type" />

		<span class="copy">
			<strong v-if="opportunity.address">{{ opportunity.address }}</strong>
			<span class="metadata">
				<span>{{ t(`opportunities-layers.${opportunity.type}`) }}</span>
				<span v-if="opportunity.city">{{ opportunity.city }}</span>
				<span v-if="opportunity.superficy">{{ formattedArea }}</span>
				<span class="price">{{ formattedPrice }}</span>
			</span>
		</span>

		<ActivityMapButton :to="{ path: '/', query: { opp: opportunity.id } }" />
	</ActivityResourceCard>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import ActivityMapButton from "@/components/activity/ActivityMapButton.vue"
import ActivityResourceCard from "@/components/activity/ActivityResourceCard.vue"
import OpportunityCardFlagIcon from "@/components/opportunities/OpportunityCardFlagIcon.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import type { Opportunity } from "@/model/DataModel"
import { formatCurrency } from "@/utils/format"
import { formatOpportunityArea } from "@/utils/opportunityUnitFields"

const props = defineProps<{
	opportunity: Opportunity
}>()

const { locale, t } = useI18n()
const { areaUnit, areaUnitSymbol } = useUnitSystem()

const formattedArea = computed(() =>
	t("activityPage.details.area", {
		area: formatOpportunityArea(props.opportunity.superficy, areaUnit.value),
		unit: areaUnitSymbol.value,
	})
)

const formattedPrice = computed(() => {
	const price = props.opportunity.price || 0

	return formatCurrency(price, locale.value)
})
</script>

<style scoped>
.opportunity-card {
	color: inherit;
	font-family: var(--font-family-primary);

	.opportunity-icon {
		flex: 0 0 auto;
	}

	.copy {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: var(--spacing-xs);
		min-width: 0;

		strong {
			overflow: hidden;
			color: var(--neutral-900);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	.metadata {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: var(--spacing-xs) var(--spacing-sm);
		min-width: 0;
		color: var(--neutral-500);
		font-size: var(--font-size-small);

		span {
			white-space: nowrap;
		}

		span + span::before {
			margin-right: var(--spacing-sm);
			color: var(--neutral-300);
			content: "·";
		}

		.price {
			color: var(--primary-green);
			font-weight: var(--font-weight-semibold);
		}
	}
}

@media (max-width: 768px) {
	.opportunity-card {
		align-items: flex-start;
	}
}
</style>
